import {
  AnchorButton,
  Button,
  Callout,
  ControlGroup,
  FormGroup,
  H6,
  HTMLSelect,
  IInputGroupProps,
  InputGroup,
  INumericInputProps,
  ITagInputProps,
  Tag,
  TagInput,
  Tooltip,
  Position,
  Intent,
  HTMLInputProps
} from '@blueprintjs/core'
import * as React from 'react'
import { DateInput } from '@blueprintjs/datetime'
import VirtualizedSelect from 'react-virtualized-select'
import { Creatable } from 'react-select'
import { formatDate, DATE_AT_TIME } from '../../../../../utils/dateHelpers'
import Client from '../../../../../client'
import { LoadableSelect } from '../../../../../components/Inputs'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import ListingDurationSelect from '../../../../../components/selects/channels/shopify/ListingDurationSelect'
import { UprightStyle } from '../../../../../types'
import { ShopifyOption } from '../../../../../types/api'
import { isRequiredError, renderError, renderErrorExceptRequired, stylizeTitle, USE_PRODUCT_VALUE } from '../Helpers'
import { GutterTooltipError } from '../GutterTooltipError'
import { ProductFormGroup } from '../ProductFormGroup'
import { debounce } from '../../../../../utils'

export type ShopifyOnChangeHandler = ({ name, value }: { name: string; value: any }) => void

interface InputProps {
  error?: any
  onChange?: any
  // eslint-disable-next-line react/no-unused-prop-types
  errorTooltipOffset?: number // Eslint doesn't like how only some usages of InputProps consume errorTooltipOffset
  // eslint-disable-next-line react/no-unused-prop-types
  errorTooltipPosition?: string
}

interface QuantityInputProps extends InputProps {
  helperText?: string
}

interface PriceInputProps extends InputProps {
  useCAD?: boolean
  showRequiredLabel?: boolean
}

interface TagInputProps extends InputProps {
  requiredValues: string[]
  useProductCategoryAsTags?: boolean
  helperTextOverride?: string
}

const style: UprightStyle = {
  calloutPadding: { margin: '8px 0' },
  tagHelperText: { marginBottom: 0 }
}

export const PriceInput = React.memo((props: PriceInputProps & INumericInputProps & HTMLInputProps) => (
  // console.log(props)
  <ProductFormGroup title="Price" error={props.error} showRequiredLabel={props.showRequiredLabel}>
    <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
      <NullSafeNumericInput
        min={0.01}
        placeholder={props.placeholder}
        clampValueOnBlur
        leftIcon="dollar"
        minorStepSize={0.01}
        selectAllOnFocus
        name="price"
        onValueChange={(_, value) => props.onChange({ name: 'price', value })}
        value={props.value || null}
        intent={props.error ? 'danger' : 'none'}
        rightElement={props.useCAD ? <Tag minimal>CAD</Tag> : undefined}
        disabled={props.disabled}
        title={props.title}
        fill
      />
    </GutterTooltipError>
  </ProductFormGroup>
))

export const CompareAtPriceInput = React.memo((props: PriceInputProps & INumericInputProps & HTMLInputProps) => (
  <ProductFormGroup title="Compare at Price" error={props.error} showRequiredLabel={props.showRequiredLabel}>
    <NullSafeNumericInput
      min={0.01}
      placeholder={props.placeholder}
      clampValueOnBlur
      leftIcon="dollar"
      minorStepSize={0.01}
      selectAllOnFocus
      name="compareAtPrice"
      onValueChange={(_, value) => props.onChange({ name: 'compareAtPrice', value })}
      value={props.value || null}
      disabled={props.disabled}
      intent={props.error ? 'danger' : 'none'}
      rightElement={props.useCAD ? <Tag minimal>CAD</Tag> : undefined}
      title={props.title}
      fill
    />
  </ProductFormGroup>
))

export const QuantityInput = React.memo((props: QuantityInputProps & INumericInputProps) => {
  return (
    <FormGroup
      label={stylizeTitle('Quantity', props.error)}
      helperText={renderError(props.error) || (props.disabled ? props.helperText : '')}
    >
      <GutterTooltipError
        isOpen={props.error}
        offsetPx={props.errorTooltipOffset}
        position={props.errorTooltipPosition}
      >
        <NullSafeNumericInput
          min={0}
          placeholder={props.placeholder}
          clampValueOnBlur
          minorStepSize={1}
          selectAllOnFocus
          name="quantity"
          onValueChange={(_, value) => props.onChange({ name: 'quantity', value })}
          value={props.value || null}
          disabled={props.disabled}
          intent={props.error ? 'danger' : 'none'}
          fill
        />
      </GutterTooltipError>
    </FormGroup>
  )
})

export const DurationInput = React.memo((props: InputProps & INumericInputProps) => (
  <FormGroup label={stylizeTitle('Duration', props.error)} helperText={renderError(props.error)}>
    <ListingDurationSelect
      placeholder="Leave blank for indefinite"
      value={props.value}
      disabled={props.disabled}
      onChange={value => props.onChange({ name: 'duration', value })}
    />
  </FormGroup>
))

export const TagsInput = React.memo((props: TagInputProps & Omit<ITagInputProps, 'onChange'>) => {
  const { requiredValues, values, onChange } = props
  const tagInputTagProps = React.useCallback(
    tagName => ({
      onRemove: requiredValues.includes(tagName)
        ? undefined
        : () => {
            onChange({ name: 'tags', value: values.filter(curr => curr !== tagName) })
          },
      minimal: true
    }),
    [requiredValues, values, onChange]
  )

  const helperText = props.helperTextOverride
    ? props.helperTextOverride
    : 'Listings product category tags are automatically included based on your settings.'

  return (
    // Unlike CollectionsInput, TagInput will NOT join values with a comma automatically. This removes any value preprocessing required
    <FormGroup
      label={stylizeTitle('Shopify Tags', props.error)}
      helperText={
        <>
          {props.useProductCategoryAsTags && <p style={style.tagHelperText}>{helperText}</p>}
          {renderError(props.error)}
        </>
      }
    >
      <TagInput
        fill
        tagProps={tagInputTagProps}
        placeholder="e.g. Outdoorsy, Jackets, Winter clothing"
        addOnBlur
        values={props.values}
        onAdd={addedValues => {
          // Array.from(new Set(...) is used here to ensure no duplicates are added to the Tags state.
          props.onChange({ name: 'tags', value: Array.from(new Set([...props.values, ...addedValues])) })
        }}
        onRemove={value =>
          props.requiredValues.includes(value.toString())
            ? () => {}
            : props.onChange({ name: 'tags', value: props.values.filter(a => a !== value) })
        }
      />
    </FormGroup>
  )
})

export const ProductTypeInput = React.memo((props: InputProps & { productValue: string } & IInputGroupProps) => {
  const usingProductValue = props.value === USE_PRODUCT_VALUE
  const useProductDisabled = !props.productValue.length
  const useProductText = useProductDisabled ? 'No product override available' : 'Use product category'
  return (
    <FormGroup
      label={stylizeTitle('Product Type', props.error)}
      helperText={
        renderError(props.error) ||
        (usingProductValue && 'Same as product category') ||
        'Click the lock to use product category'
      }
      intent={props.error ? 'danger' : 'none'}
    >
      <ControlGroup>
        <InputGroup
          onChange={e => props.onChange({ name: 'productType', value: e.target.value })}
          name="productType"
          value={(usingProductValue ? props.productValue : props.value) || ''}
          fill
          disabled={usingProductValue}
        />
        <Tooltip content={usingProductValue ? 'Override product type' : useProductText}>
          <AnchorButton
            icon={usingProductValue ? 'lock' : 'unlock'}
            intent="primary"
            minimal={!usingProductValue}
            disabled={useProductDisabled}
            onClick={() =>
              props.onChange({
                name: 'productType',
                value: usingProductValue ? props.productValue || '' : USE_PRODUCT_VALUE
              })
            }
          />
        </Tooltip>
      </ControlGroup>
    </FormGroup>
  )
})

export const VendorInput = (props: InputProps & { value: string; marketAccountId: number } & IInputGroupProps) => {
  const { value } = props
  const [loading, setLoading] = React.useState(null)
  const [vendorOptions, setVendorOptions] = React.useState(null)
  const checkVendor = debounce(async (vendor: string) => {
    try {
      setLoading(true)
      const data = await Client.getShopifyVendorsSearch(props.marketAccountId, vendor)

      let vendors = []
      if (data.data?.vendors?.length > 0) {
        vendors = data.data.vendors
      }
      setVendorOptions(vendors)
    } catch (e) {
      // fail silently - this is a small value-add, not critical to workflow
      // eslint-disable-next-line no-console
      console.log(e)
    } finally {
      setLoading(false)
    }
  }, 500)

  const checkvendorMemo = React.useCallback((vendor: string) => {
    checkVendor(vendor)
    // Might be a correct way to memoize this, but inlining checkvendor here will break the debouncing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeHandler = v => {
    props.onChange({ name: 'vendor', value: '' })
    checkvendorMemo(v)
  }

  return (
    <>
      <FormGroup
        className="nuke-top-margin-loadable-select"
        label={stylizeTitle('Vendor Search Input', props.error)}
        helperText={renderError(props.error)}
        labelInfo="Required"
        intent={props.error ? 'danger' : 'none'}
      >
        <InputGroup
          defaultValue={value}
          placeholder="Start typing the name of the vendor"
          onChange={e => changeHandler(e.target.value)}
          rightElement={<Button disabled={!!value} loading={loading} minimal intent={Intent.WARNING} icon="search" />}
        />
      </FormGroup>
      {vendorOptions && (
        <FormGroup
          className="nuke-top-margin-loadable-select"
          label={stylizeTitle('Vendor', props.error)}
          helperText="If the results do not include the vendor you are looking for, please add it manually by typing in this box or try another search term"
          intent={props.error ? 'danger' : 'none'}
        >
          <VirtualizedSelect
            isClearable
            selectComponent={Creatable}
            value={value ? { label: value, value } : ''}
            placeholder="Select a vendor or type to add a new one"
            onChange={v => {
              props.onChange({ name: 'vendor', value: v?.value })
            }}
            options={vendorOptions.map(s => ({ label: s, value: s }))}
          />
        </FormGroup>
      )}
    </>
  )
}

export const GtinBarcodeInput = React.memo((props: InputProps & IInputGroupProps) => (
  <FormGroup label={stylizeTitle('Barcode (ISBN, UPC, GTIN)', props.error)} helperText={renderError(props.error)}>
    <InputGroup
      placeholder={props.placeholder}
      name="barcode"
      // GTIN/MPN/ISBN is also known as listings_shopify#barcode
      onChange={e => props.onChange({ name: 'barcode', value: e.target.value })}
      value={props.value}
      disabled={props.disabled}
      intent={props.error ? 'danger' : 'none'}
      fill
    />
  </FormGroup>
))

export const CollectionsInput = React.memo((props: InputProps & { value: string; marketAccountId: number }) => {
  /* TODO(shopify): - When we support multiple markets, we'll need to replace the loadable select
                      so that we can refetch on marketAccountId change */

  const fetchCollections = React.useCallback(
    () => Client.shopifyCollections(props.marketAccountId),
    [props.marketAccountId]
  )
  return (
    <FormGroup label={stylizeTitle('Custom Collections', props.error)} helperText={renderError(props.error)}>
      {/* Unlike TagInput, LoadableSelect will join values with a comma automatically. This is broken up in ProductSerializer  */}
      <LoadableSelect
        multi
        simpleValue
        autoload={props.value.length}
        load={fetchCollections}
        process={d => d.customCollections.map(s => ({ value: s.id, label: s.title }))}
        value={props.value}
        onChange={value => props.onChange({ name: 'rawCollections', value })}
      />
    </FormGroup>
  )
})

export const LocationsInput = React.memo(
  (
    props: InputProps & { value: number; disabledReason?: string; marketAccountId: number; showRequiredLabel?: boolean }
  ) => {
    const fetchLocations = React.useCallback(
      () => Client.getShopifySettings(props.marketAccountId),
      [props.marketAccountId]
    )

    const labelInfo = props.showRequiredLabel ? 'Required' : null

    return (
      <FormGroup // ProductFormGroup is tricky to use here
        label={stylizeTitle('Location', props.error)}
        labelInfo={isRequiredError(props.error) ? renderError(labelInfo) : labelInfo}
        helperText={renderErrorExceptRequired(props.error, labelInfo) || props.disabledReason}
        className="float-right-formgroup-label"
      >
        <GutterTooltipError isOpen={props.error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <LoadableSelect
            simpleValue
            noLabel
            clearable={false}
            autoload={props.value}
            disabled={!!props.disabledReason}
            load={fetchLocations}
            intent={props.error && 'danger'}
            process={d => d.account?.shopifyLocations.map(s => ({ value: s.id, label: s.name })) || []}
            value={props.value}
            onChange={value => props.onChange({ name: 'locationId', value })}
          />
        </GutterTooltipError>
      </FormGroup>
    )
  }
)

export const TemplateSuffixInput = React.memo((props: InputProps & IInputGroupProps) => (
  <FormGroup label={stylizeTitle('Template Suffix', props.error)} helperText={renderError(props.error)}>
    <InputGroup
      placeholder={props.placeholder}
      name="templateSuffix"
      onChange={e => props.onChange({ name: 'templateSuffix', value: e.target.value })}
      value={props.value}
      disabled={props.disabled}
      intent={props.error ? 'danger' : 'none'}
      fill
    />
  </FormGroup>
))

const MAX_OPTION_COUNT = 3
export const OptionsInput = React.memo((props: InputProps & { values: ShopifyOption[] }) => {
  const { values, onChange, error } = props
  const onRemoveOption = React.useCallback(
    deletedIndex => () => onChange({ name: 'options', value: values.filter((_, i) => i !== deletedIndex) }),
    [values, onChange]
  )
  const onAppendOption = React.useCallback(
    () => onChange({ name: 'options', value: [...values, { name: '', value: '' }] }),
    [values, onChange]
  )
  const onInputChange = React.useCallback(
    (index, field) => e =>
      onChange({
        name: 'options',
        value: values.map((entry, currentIndex) =>
          index === currentIndex ? { ...entry, [field]: e.currentTarget.value } : entry
        )
      }),
    [values, onChange]
  )

  return (
    <>
      <H6>Options</H6>
      {error && (
        <Callout style={style.calloutPadding} intent="danger">
          Options {error}
        </Callout>
      )}
      {values.map((option, i) => (
        <ControlGroup className="shopify-options-input-group">
          <FormGroup label="Name">
            <InputGroup onChange={onInputChange(i, 'name')} value={option.name} placeholder="Size" />
          </FormGroup>
          <FormGroup label="Value">
            <InputGroup onChange={onInputChange(i, 'value')} value={option.value} fill placeholder="Small" />
          </FormGroup>
          <FormGroup>
            <Button minimal text="Remove" onClick={onRemoveOption(i)} />
          </FormGroup>
        </ControlGroup>
      ))}
      {values.length < MAX_OPTION_COUNT && (
        <Button onClick={onAppendOption} text={props.values.length ? 'Add another option' : 'Add an option'} />
      )}
    </>
  )
})

const scheduleTimePickerStyle: UprightStyle = {
  select: { maxWidth: 115 }
}

interface ScheduleTimePickerProps extends InputProps {
  value: string
  onChange: ShopifyOnChangeHandler
  disabled?: boolean
}

export const ScheduleTimePicker: React.FC<ScheduleTimePickerProps> = ({ value, onChange, error, disabled }) => {
  const [isScheduled, setIsScheduled] = React.useState<boolean>(!!value)

  return (
    <FormGroup label={stylizeTitle('Start Time', error)} intent={error ? 'danger' : 'none'}>
      <ControlGroup fill vertical={false}>
        <div style={scheduleTimePickerStyle.select} className="schedule-time-picker-select-container">
          <HTMLSelect
            onChange={e => {
              setIsScheduled(e.target.value === 'Scheduled')
              if (e.target.value === 'Immediately') {
                onChange({ value: null, name: 'scheduleTime' })
              }
            }}
            disabled={disabled}
            options={['Immediately', 'Scheduled'].map(o => ({ label: o, value: o }))}
            value={isScheduled ? 'Scheduled' : 'Immediately'}
          />
        </div>
        <DateInput
          formatDate={date => (disabled ? '' : formatDate(date, DATE_AT_TIME))}
          parseDate={str => new Date(str)}
          value={value ? new Date(value) : null}
          onChange={date => onChange({ name: 'scheduleTime', value: date })}
          timePrecision="minute"
          timePickerProps={{ useAmPm: true }}
          disabled={disabled || !isScheduled}
          minDate={new Date()}
          placeholder={!isScheduled ? 'List Immediately' : 'Schedule Listing'}
          closeOnSelection={false}
        />
      </ControlGroup>
    </FormGroup>
  )
}

export const UPCInput = React.memo(
  (props: InputProps & IInputGroupProps): JSX.Element => (
    <ProductFormGroup title="UPC" error={props.error}>
      <InputGroup
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          props.onChange({ name: 'barcode', value: e.target.value })
        }
        name="barcode"
        value={props.value || ''}
        placeholder="Enter the UPC number"
        intent={props.error ? Intent.DANGER : Intent.NONE}
      />
    </ProductFormGroup>
  )
)
